import { createAxiosInstance } from './apiUtils';

export type CapturedSample = { captured_at: string; record: any };
export type DataSamplesResponse = {
  samples: Record<string, CapturedSample[]>;
  capture_enabled: Record<string, boolean>;
  max_samples?: number;
  ttl_seconds?: number;
};

/** The execute route answered, but not with samples. */
export class DataSamplesFormatError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DataSamplesFormatError';
  }
}

const isSamplesBody = (body: any): boolean =>
  body != null &&
  typeof body === 'object' &&
  body.samples != null &&
  typeof body.samples === 'object';

/** The handler's return value. The execute route answers with it as-is (reference
 *  widgets read `resp.data.options`); an envelope around it is accepted too. */
export function unwrapDataSamples(body: any): DataSamplesResponse {
  const found = [body, body?.data, body?.result, body?.response].find(
    isSamplesBody,
  );
  if (!found) throw new DataSamplesFormatError('no samples in the response');
  return {
    ...found,
    samples: found.samples,
    capture_enabled:
      found.capture_enabled && typeof found.capture_enabled === 'object'
        ? found.capture_enabled
        : {},
  };
}

/** Records the connector captured for the transformation editor. Never cached:
 *  every call (Refresh included) asks the connector again. */
export async function fetchDataSamples(
  token: string,
  integrationId: string,
  actionValue: string,
  outputType?: string,
): Promise<DataSamplesResponse> {
  const axiosInstance = await createAxiosInstance(token);
  const resp = await axiosInstance.post(
    `/v2/integrations/${encodeURIComponent(integrationId)}/actions/${encodeURIComponent(actionValue)}/execute/`,
    { config_overrides: outputType ? { output_type: outputType } : {} },
    { timeout: 15000 }, // Cloud Run cold starts
  );
  return unwrapDataSamples(resp.data);
}
